import {
  Button,
  Card,
  CardContent,
  cn,
  Skeleton,
  statusOkDotClass,
} from '@up2stay/up2promo-ui';

import { shellPaths } from '@/core/router/app-paths';

import type {
  DashboardHealthMix,
  DashboardHealthRow,
  DashboardHealthStatus,
} from './dashboard-status-types';
import {
  statusBoardCardClass,
  statusBoardCardPadClass,
  statusBoardMetaClass,
  statusBoardSectionTitleClass,
} from './dashboard-board-surfaces';

function statusDotClass(status: DashboardHealthStatus): string {
  switch (status) {
    case 'ok':
      return statusOkDotClass;
    case 'critical':
    case 'sync':
      return 'shrink-0 rounded-full bg-destructive';
    case 'warning':
    case 'setup':
      return 'shrink-0 rounded-full bg-amber-500';
    default:
      return 'shrink-0 rounded-full bg-muted-foreground/40';
  }
}

function actionForRow(
  row: DashboardHealthRow,
  labels: { reply: string; connectGoogle: string; open: string },
): { label: string; primary: boolean } {
  if (row.needsGoogleConnect || row.status === 'setup') {
    return { label: labels.connectGoogle, primary: true };
  }
  if (row.status === 'sync') {
    return { label: labels.connectGoogle, primary: true };
  }
  if (
    row.status === 'critical' ||
    (row.status === 'warning' && row.rating != null)
  ) {
    return { label: labels.reply, primary: false };
  }
  // Connected + zero reviews (or healthy) → open, never “Connect Google”.
  return { label: labels.open, primary: false };
}

export function DashboardStandorteCard({
  rows,
  mix,
  isLoading,
  isMobile,
  onOpen,
  onViewAll,
  labels,
}: {
  rows: DashboardHealthRow[];
  mix: DashboardHealthMix;
  isLoading: boolean;
  isMobile: boolean;
  onOpen: (href: string) => void;
  onViewAll: () => void;
  labels: {
    title: string;
    viewAll: string;
    empty: string;
    emptyCta: string;
    colLocation: string;
    colScore: string;
    colRating: string;
    colScans: string;
    reply: string;
    connectGoogle: string;
    open: string;
    mixOk: string;
    mixAttention: string;
    canManage: boolean;
  };
}) {
  if (isLoading) {
    return (
      <Card className={cn(statusBoardCardClass, 'h-full')} aria-busy="true">
        <CardContent className={cn('space-y-3', statusBoardCardPadClass)}>
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <section
      aria-labelledby="dashboard-standorte-heading"
      className="flex h-full min-w-0 flex-col"
    >
      <Card className={cn(statusBoardCardClass, 'h-full')}>
        <CardContent
          className={cn('flex flex-col gap-3', statusBoardCardPadClass)}
        >
          <div className="flex flex-wrap items-center gap-2 md:gap-3">
            <h2
              id="dashboard-standorte-heading"
              className={cn('min-w-0 flex-1', statusBoardSectionTitleClass)}
            >
              {labels.title}
            </h2>
            {!isMobile && mix.ok > 0 ? (
              <span
                className={cn(
                  'inline-flex items-center gap-1.5',
                  statusBoardMetaClass,
                )}
              >
                <span className={cn('size-2', statusOkDotClass)} />
                {mix.ok} {labels.mixOk}
              </span>
            ) : null}
            {!isMobile && mix.attention + mix.critical > 0 ? (
              <span
                className={cn(
                  'inline-flex items-center gap-1.5',
                  statusBoardMetaClass,
                )}
              >
                <span className="size-2 shrink-0 rounded-full bg-amber-500" />
                {mix.attention + mix.critical} {labels.mixAttention}
              </span>
            ) : null}
            <Button
              type="button"
              variant="ghostAccent"
              size="sm"
              className="h-8 px-2 text-xs"
              onClick={onViewAll}
            >
              {labels.viewAll}
            </Button>
          </div>

          {rows.length === 0 ? (
            <div className="flex flex-col items-start gap-3 py-4">
              <p className="text-sm text-muted-foreground">{labels.empty}</p>
              {labels.canManage ? (
                <Button
                  size="md"
                  className="min-h-11"
                  onClick={() => onOpen(shellPaths.moreWorkspaceLocationCreate)}
                >
                  {labels.emptyCta}
                </Button>
              ) : null}
            </div>
          ) : isMobile ? (
            <ul className="flex flex-col">
              {rows.map((row) => {
                const action = actionForRow(row, labels);
                return (
                  <li
                    key={row.id}
                    className="flex flex-col gap-2 border-t border-border/60 py-3 first:border-t-0 first:pt-0"
                  >
                    <div className="flex items-start gap-2.5">
                      <span
                        className={cn(
                          'mt-1.5 size-2',
                          statusDotClass(row.status),
                        )}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-[14px] font-medium text-foreground">
                          {row.name}
                        </p>
                        <p className="text-[12px] text-muted-foreground">
                          {[row.scoreLabel, row.rating?.toFixed(1), row.meta]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant={action.primary ? 'primary' : 'outline'}
                      className="min-h-10 w-full"
                      onClick={() => onOpen(row.href)}
                    >
                      {action.label}
                    </Button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="overflow-x-auto">
              <div className="grid min-w-[640px] grid-cols-[minmax(0,2.2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_150px] gap-3 pb-2 text-[12px] text-muted-foreground">
                <span>{labels.colLocation}</span>
                <span>{labels.colScore}</span>
                <span>{labels.colRating}</span>
                <span>{labels.colScans}</span>
                <span />
              </div>
              {rows.map((row) => {
                const action = actionForRow(row, labels);
                return (
                  <div
                    key={row.id}
                    className="grid min-w-[640px] grid-cols-[minmax(0,2.2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_150px] items-center gap-3 border-t border-border/60 py-3"
                  >
                    <div className="flex min-w-0 items-center gap-2.5">
                      <span
                        className={cn(
                          'size-2 shrink-0',
                          statusDotClass(row.status),
                        )}
                      />
                      <div className="min-w-0">
                        <p className="truncate text-[14px] font-medium text-foreground">
                          {row.name}
                        </p>
                        {row.meta ? (
                          <p className="truncate text-[12px] text-muted-foreground">
                            {row.meta}
                          </p>
                        ) : null}
                      </div>
                    </div>
                    <span className="font-mono text-[14px] tabular-nums text-foreground">
                      {row.scoreLabel ?? '—'}
                    </span>
                    <span className="font-mono text-[14px] tabular-nums text-foreground">
                      {row.rating != null
                        ? `${row.rating.toFixed(1).replace('.', ',')} ★`
                        : '—'}
                    </span>
                    <span className="font-mono text-[14px] tabular-nums text-muted-foreground">
                      —
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant={action.primary ? 'primary' : 'outline'}
                      className="h-[34px] rounded-[9px]"
                      onClick={() => onOpen(row.href)}
                    >
                      {action.label}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
